import type { WidgetV2MountOptions } from './types'
import { extraServiceBillingLabel } from './extraServiceBillingLabel'
import { formatYmd, parseYmd, todayYmd } from './calendarDate'
import { enhanceDateRangePicker } from './date-range-picker'
import { enhanceNumberSteppers } from './number-stepper'
import {
  buildConstraintCtx,
  endDisabledReason,
  isEndDisabled,
  isStartDisabled,
  startDisabledReason,
  type WidgetStayConstraints,
} from './stay-calendar-constraints'

type WidgetExtraService = {
  id: string
  code: string
  name: string
  description?: string | null
  billing_unit: string
  unit_price_cents: number
  required: boolean
  min_quantity: number
  max_quantity: number
  effective_max_quantity: number
}

type WidgetStaySeasonLine = {
  season_name: string
  nights: number
  unit_price_cents_per_night: number
  line_total_cents: number
  line_gross_cents?: number
  /** Wenn >0: Listenbrutto pro Zeile (vor UO/OO, wie im Backoffice). */
  line_gross_list_cents?: number
  unit_list_price_cents_per_night?: number
}

type WidgetDiscountStep = {
  kind: string
  value: number
  note?: string | null
  discount_cents: number
}

function isUnderoccupancyStep(step: WidgetDiscountStep): boolean {
  return step.kind === 'underoccupancy' || String(step.note ?? '').includes('Unterbelegung')
}

/** API-Hinweise mit „ · “ als Zeilen (analog App-Preisübersicht). */
function noteBulletParts(note: string | null | undefined): string[] {
  const raw = String(note ?? '').trim()
  if (!raw) return []
  return raw
    .split(/\s*[·•]\s*/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0 && !/^[\s·•]+$/.test(s))
}

function underoccupancyNoteFromSteps(steps: WidgetDiscountStep[] | undefined): string | null {
  for (const s of steps ?? []) {
    if (!isUnderoccupancyStep(s)) continue
    const n = String(s.note ?? '').trim()
    if (n && n !== 'Unterbelegungsabschlag') return n
  }
  return null
}

type WidgetVatBreakdownLine = {
  vat_rate_bps: number
  net_cents: number
  vat_cents: number
  gross_cents: number
}

export type WidgetPricingBreakdown = {
  stay_lines: WidgetStaySeasonLine[]
  stay_subtotal_cents: number
  extra_lines: Array<{
    code: string
    name: string
    quantity: number
    billing_unit: string
    unit_price_cents: number
    line_total_cents: number
    line_gross_cents?: number
  }>
  extras_subtotal_cents: number
  grand_total_cents: number
  /** Wie in der App: Netto/Brutto-Steuer der Positionskette vor manuellen Rabatten. */
  grand_total_net_cents?: number
  grand_total_vat_cents?: number
  discount_cents: number
  discount_steps: WidgetDiscountStep[]
  underoccupancy_discount_cents: number
  /** Optional; gleiche API wie Backoffice. */
  overoccupancy_surcharge_cents?: number
  overoccupancy_surcharge_note?: string | null
  credit_cents: number
  total_after_discount_cents: number
  nights_counted: number
  vat_breakdown_by_rate: WidgetVatBreakdownLine[]
  notes: string[]
  /** Kanonische Ketten-Größen (gleiche Logik wie Backoffice-API) */
  stay_list_cents?: number
  occupancy_sondertarif_net_cents?: number
  stay_gross_before_underoccupancy_cents?: number
  stay_gross_before_overoccupancy_cents?: number
  /** Sicherheitskaution in Cent; nur wenn > 0 gesetzt. */
  security_deposit_cents?: number
  /** Tage vor Anreise, bis zu denen die Kaution fällig ist. */
  security_deposit_due_days_before_arrival?: number
}

function esc(s: string): string {
  return s
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
}

function euro(cents: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format((cents || 0) / 100)
}

function debounce(fn: () => void, delayMs: number): () => void {
  let t: number | undefined
  return () => {
    if (t != null) window.clearTimeout(t)
    t = window.setTimeout(() => {
      fn()
    }, delayMs)
  }
}

async function fetchWidgetExtraServices(
  apiBaseUrl: string,
  token: string,
  propertyId: string,
  query: Record<string, string>,
): Promise<WidgetExtraService[]> {
  const base = apiBaseUrl.replace(/\/$/, '')
  const sp = new URLSearchParams(query)
  const res = await fetch(
    `${base}/api/v1/public/widget/properties/${encodeURIComponent(propertyId)}/extra-services?${sp.toString()}`,
    { method: 'GET', headers: { Accept: 'application/json', Authorization: `Bearer ${token}` } },
  )
  if (!res.ok) throw new Error(`${res.status} ${await res.text()}`)
  return (await res.json()) as WidgetExtraService[]
}

async function previewWidgetPricing(
  apiBaseUrl: string,
  token: string,
  propertyId: string,
  body: {
    adult_count?: number
    child_count?: number
    stay_from?: string
    stay_to?: string
    extra_lines: Array<{ extra_service_id: string; quantity: number }>
    /** Rabattcode wie im Backoffice, nach manuellen Rabatten. */
    promo_code?: string
    /** Fuer an E-Mail gebundene Codes: Vorschau mit derselben Adresse. */
    customer_email?: string
  },
): Promise<WidgetPricingBreakdown> {
  const base = apiBaseUrl.replace(/\/$/, '')
  const res = await fetch(`${base}/api/v1/public/widget/properties/${encodeURIComponent(propertyId)}/preview-pricing`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(`${res.status} ${await res.text()}`)
  return (await res.json()) as WidgetPricingBreakdown
}

async function prepareWidgetCheckout(
  apiBaseUrl: string,
  token: string,
  body: Record<string, unknown>,
): Promise<{ checkout_token: string; customer_status: string; price_breakdown: WidgetPricingBreakdown }> {
  const base = apiBaseUrl.replace(/\/$/, '')
  const res = await fetch(`${base}/api/v1/public/widget/checkout/prepare`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(`${res.status} ${await res.text()}`)
  return (await res.json()) as { checkout_token: string; customer_status: string; price_breakdown: WidgetPricingBreakdown }
}

async function confirmWidgetCheckout(
  apiBaseUrl: string,
  token: string,
  checkoutToken: string,
): Promise<{ reference_id: string; payment_mode: string; redirect_url: string; fallback_reason?: string | null }> {
  const base = apiBaseUrl.replace(/\/$/, '')
  const res = await fetch(`${base}/api/v1/public/widget/checkout/confirm`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ checkout_token: checkoutToken }),
  })
  if (!res.ok) throw new Error(`${res.status} ${await res.text()}`)
  return (await res.json()) as { reference_id: string; payment_mode: string; redirect_url: string; fallback_reason?: string | null }
}

async function fetchPropertiesQuick(
  apiBaseUrl: string,
  token: string,
  query: Record<string, string>,
): Promise<{ items: Array<{ id: string }> }> {
  const base = apiBaseUrl.replace(/\/$/, '')
  const sp = new URLSearchParams({ page_size: '50', ...query })
  const res = await fetch(`${base}/api/v1/public/widget/properties?${sp.toString()}`, {
    method: 'GET',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  })
  if (!res.ok) throw new Error(`${res.status} ${await res.text()}`)
  return (await res.json()) as { items: Array<{ id: string }> }
}

function stayListenCentsW(p: WidgetPricingBreakdown): number {
  if (p.stay_list_cents != null) return p.stay_list_cents
  const f = Math.max(0, p.stay_subtotal_cents ?? 0)
  const uo = p.underoccupancy_discount_cents ?? 0
  const oo = p.overoccupancy_surcharge_cents ?? 0
  if (uo <= 0 && oo <= 0) return f
  return Math.max(0, f - oo + uo)
}

function belegungSondertarifNetCentsW(p: WidgetPricingBreakdown): number {
  if (p.occupancy_sondertarif_net_cents != null) return p.occupancy_sondertarif_net_cents
  const uo = p.underoccupancy_discount_cents ?? 0
  const oo = p.overoccupancy_surcharge_cents ?? 0
  if (uo <= 0 && oo <= 0) return 0
  return oo - uo
}

function formatSignedSondertarifCents(c: number): { display: string; className: string } {
  if (c < 0) return { display: '−' + euro(-c), className: 'uzw-v2-pb-sond--neg' }
  if (c > 0) return { display: '+' + euro(c), className: 'uzw-v2-pb-sond--pos' }
  return { display: euro(0), className: 'uzw-v2-pb-sond--null' }
}

function stayLineCentsGross(l: WidgetStaySeasonLine): number {
  const list = l.line_gross_list_cents ?? 0
  if (list > 0) return list
  return Math.max(0, l.line_gross_cents ?? 0, l.line_total_cents ?? 0)
}

function stayLineUnitPerNightCents(l: WidgetStaySeasonLine): number {
  const u = l.unit_list_price_cents_per_night ?? 0
  if (u > 0) return u
  if ((l.line_gross_list_cents ?? 0) > 0 && l.nights > 0) {
    return Math.floor((l.line_gross_list_cents as number) / l.nights)
  }
  return l.unit_price_cents_per_night
}

/** Nächte-Brutto wie in der API: `stay_subtotal_cents`. */
function widgetStayOvernightDisplayCents(p: WidgetPricingBreakdown): number {
  return Math.max(0, p.stay_subtotal_cents ?? 0)
}

/** Wie in der App (Preisübersicht, aufklappbar hinter Sondertarif-Zeile). */
function renderSondertarifExpandAppStyle(pricing: WidgetPricingBreakdown): string {
  const uo = Math.max(0, pricing.underoccupancy_discount_cents ?? 0)
  const over = Math.max(0, pricing.overoccupancy_surcharge_cents ?? 0)
  if (uo <= 0 && over <= 0) return ''

  const underNote = underoccupancyNoteFromSteps(pricing.discount_steps)
  const underBullets = noteBulletParts(underNote)
    .map(
      (part) =>
        `<li class="uzw-v2-sr-li"><span class="uzw-v2-sr-li-dot" aria-hidden="true">·</span><span>${esc(part)}</span></li>`,
    )
    .join('')
  const underList =
    underBullets.length > 0
      ? `<ul class="uzw-v2-sr-ul">${underBullets}</ul>`
      : `<p class="uzw-v2-sr-fallback">Abschlag auf den Listen-Übernachtungsteil (Zusatzleistungen unverändert).</p>`

  const overNote = pricing.overoccupancy_surcharge_note
  const overBullets = noteBulletParts(overNote)
    .map(
      (part) =>
        `<li class="uzw-v2-sr-li"><span class="uzw-v2-sr-li-dot" aria-hidden="true">·</span><span>${esc(part)}</span></li>`,
    )
    .join('')
  const overList =
    overBullets.length > 0
      ? `<ul class="uzw-v2-sr-ul">${overBullets}</ul>`
      : `<p class="uzw-v2-sr-fallback">Zuschlag auf den Listen-Übernachtungsteil (Zusatzleistungen unverändert).</p>`

  const parts: string[] = []
  if (uo > 0) {
    parts.push(`<div class="uzw-v2-sr-card uzw-v2-sr-card--under">
  <div class="uzw-v2-sr-card-inner">
    <div class="uzw-v2-sr-text">
      <p class="uzw-v2-sr-title">Unterbelegungsabschlag</p>
      ${underList}
    </div>
    <span class="uzw-v2-sr-amt uzw-v2-sr-amt--minus" aria-label="Unterbelegung">−${euro(uo)}</span>
  </div>
</div>`)
  }
  if (over > 0) {
    parts.push(`<div class="uzw-v2-sr-card uzw-v2-sr-card--over">
  <div class="uzw-v2-sr-card-inner">
    <div class="uzw-v2-sr-text">
      <p class="uzw-v2-sr-title">Aufbettungszuschlag</p>
      ${overList}
    </div>
    <span class="uzw-v2-sr-amt uzw-v2-sr-amt--plus" aria-label="Aufbettung">+${euro(over)}</span>
  </div>
</div>`)
  }
  return `<div class="uzw-v2-sr-exp">
  <p class="uzw-v2-sr-exp-intro">Einzelbeträge, Hinweise und Rechenwege (nur Übernachtung).</p>
  <div class="uzw-v2-sonderregeln-cards uzw-v2-sonderregeln-cards--plain">${parts.join('')}</div>
</div>`
}

function extraLineCentsGross(l: {
  line_gross_cents?: number
  line_total_cents: number
}): number {
  return Math.max(0, l.line_gross_cents ?? 0, l.line_total_cents ?? 0)
}

function extrasBlockDisplayCents(p: WidgetPricingBreakdown): number {
  return Math.max(0, p.extras_subtotal_cents ?? 0)
}

/** Netto/USt. wie in der App: bei manuellem Rabatt ggf. aus USt-Tabelle, sonst API-Felder. */
function netVatDisplayCents(
  p: WidgetPricingBreakdown,
): { net: number; vat: number; vatIsAfterDiscount: boolean } {
  const vatRows = p.vat_breakdown_by_rate ?? []
  if ((p.discount_cents ?? 0) > 0 && vatRows.length > 0) {
    const net = vatRows.reduce((s, v) => s + (v.net_cents ?? 0), 0)
    const vat = vatRows.reduce((s, v) => s + (v.vat_cents ?? 0), 0)
    return { net, vat, vatIsAfterDiscount: true }
  }
  return {
    net: p.grand_total_net_cents ?? 0,
    vat: p.grand_total_vat_cents ?? 0,
    vatIsAfterDiscount: false,
  }
}

function renderPricingHtml(pricing: WidgetPricingBreakdown): string {
  const stayLines = pricing.stay_lines ?? []
  const listenCents = stayListenCentsW(pricing)
  const sondNet = belegungSondertarifNetCentsW(pricing)
  const stayNachtCents = widgetStayOvernightDisplayCents(pricing)
  const uo0 = (pricing.underoccupancy_discount_cents ?? 0) > 0
  const oo0 = (pricing.overoccupancy_surcharge_cents ?? 0) > 0
  const hasStaySonder = uo0 || oo0
  const hasOvernightAdj = uo0 || oo0
  const stayRows = stayLines
    .map(
      (l) =>
        `<tr><td>${esc(l.season_name)}</td><td>${l.nights}</td><td>${euro(stayLineUnitPerNightCents(l))}</td><td>${euro(
          stayLineCentsGross(l),
        )}</td></tr>`,
    )
    .join('')

  const extrasSub = extrasBlockDisplayCents(pricing)
  const hasExtras = extrasSub > 0
  const grand = pricing.grand_total_cents ?? 0
  const stayNachtHint = hasStaySonder
    ? '<p class="uzw-v2-stay-inkl-hint" role="note">Hier: Listenpreise je Saison (ohne verteilte Unter- oder Aufbettung). Sondertarif: die folgende Zeile.</p>'
    : ''
  const stayTableOnly =
    stayRows.length > 0
      ? `<div class="uzw-v2-pb-stay-bod">
  <p class="uzw-v2-pb-saison-cap">Saisonzeilen</p>
  <table class="uzw-v2-price-table uzw-v2-pb-embed-tbl">
    <thead><tr><th>Saison</th><th>Nächte</th><th>€/Nacht</th><th>Summe</th></tr></thead>
    <tbody>${stayRows}</tbody>
  </table>${stayNachtHint}
</div>`
      : '<p class="uzw-v2-meta">Keine Saisonzeilen.</p>'

  const sondertarifSigned = formatSignedSondertarifCents(sondNet)
  const sondExpand = renderSondertarifExpandAppStyle(pricing)

  const pbDtl = (label: string, amount: string, open: boolean, body: string, title?: string): string => {
    const t = title ? ` title="${esc(title)}"` : ''
    return `<details class="uzw-v2-pb-dtl"${open ? ' open' : ''}>
  <summary class="uzw-v2-pb-sum"${t}>
    <div class="uzw-v2-pb-sum-h">
      <span class="uzw-v2-pb-t">${esc(label)}</span>
      <span class="uzw-v2-pb-ch" aria-hidden="true">▾</span>
    </div>
    <div class="uzw-v2-pb-m uzw-v2-nowrap uzw-v2-pb-m--block">${amount}</div>
  </summary>
  <div class="uzw-v2-pb-bod">${body}</div>
</details>`
  }

  const pbDtlSonder = (): string =>
    `<details class="uzw-v2-pb-dtl" title="Sondertarife: Unterbelegung &amp; Aufbettung, netto">
  <summary class="uzw-v2-pb-sum">
    <div class="uzw-v2-pb-sum-h">
      <span class="uzw-v2-pb-t">Sondertarife <span class="uzw-v2-pb-qual">(netto)</span></span>
      <span class="uzw-v2-pb-ch" aria-hidden="true">▾</span>
    </div>
    <div class="uzw-v2-pb-m uzw-v2-nowrap ${sondertarifSigned.className} uzw-v2-pb-m--block">${sondertarifSigned.display}</div>
  </summary>
  <div class="uzw-v2-pb-bod uzw-v2-pb-bod--sond">${sondExpand || '<p class="uzw-v2-meta">—</p>'}</div>
</details>`

  const pbStat = (label: string, amount: string, muted: boolean, longTitle?: string): string => {
    const t = longTitle ? ` title="${esc(longTitle)}"` : ''
    return `<div class="uzw-v2-pb-stat${muted ? ' uzw-v2-pb-stat--muted' : ''}" role="presentation"${t}>
  <span class="uzw-v2-pb-t">${esc(label)}</span>
  <span class="uzw-v2-pb-m uzw-v2-nowrap">${amount}</span>
</div>`
  }

  let ablaufCore = ''
  if (stayLines.length > 0) {
    if (hasStaySonder) {
      ablaufCore += pbDtl('Übernachtung (Nächte, Listensumme)', euro(listenCents), true, stayTableOnly)
      ablaufCore += pbStat('Gesamt', euro(listenCents), true)
      if (hasOvernightAdj) {
        ablaufCore += pbDtlSonder()
      }
      if (hasOvernightAdj && hasExtras) {
        ablaufCore += pbStat('Nächte (brutto)', euro(stayNachtCents), true, 'Gesamt nur Nächte, brutto (mit Sondertarifen)')
      }
    } else {
      ablaufCore += pbDtl('Übernachtung (Nächte)', euro(stayNachtCents), true, stayTableOnly)
      if (hasExtras) {
        ablaufCore += pbStat('Gesamt', euro(stayNachtCents), true)
      }
    }
  } else {
    ablaufCore = '<p class="uzw-v2-meta uzw-v2-pb-nov">Keine Übernachtungspreise hinterlegt.</p>'
  }

  if (hasExtras) {
    const extrasRows = (pricing.extra_lines ?? [])
      .map((l) => {
        const g = extraLineCentsGross(l)
        const bu = extraServiceBillingLabel(l.billing_unit)
        const nameBlock = bu
          ? `<div class="uzw-v2-extra-name">${esc(l.name)}</div>
          <div class="uzw-v2-extra-sub">${esc(bu)}</div>`
          : `<div class="uzw-v2-extra-name">${esc(l.name)}</div>`
        return `<tr>
        <td>${nameBlock}
        </td>
        <td class="uzw-v2-num">${l.quantity}</td>
        <td class="uzw-v2-num uzw-v2-nowrap">${euro(g)}</td>
      </tr>`
      })
      .join('')

    const extrasBody = `<p class="uzw-v2-pb-extras-cap">Einzelne Zusatzleistungen</p>
<table class="uzw-v2-price-table uzw-v2-price-table--extras uzw-v2-pb-embed-tbl">
  <thead>
    <tr><th>Leistung</th><th class="uzw-v2-th-num">Menge</th><th class="uzw-v2-th-num">Summe</th></tr>
  </thead>
  <tbody>
    ${extrasRows || '<tr><td colspan="3" class="uzw-v2-meta">Keine Zusatzleistungen gewählt.</td></tr>'}
  </tbody>
  <tfoot><tr><td colspan="3" class="uzw-v2-pb-extras-foot">
    <div class="uzw-v2-pb-extras-sum">
      <span class="uzw-v2-pb-extras-sum-l">Zwischensumme Zusatz</span>
      <span class="uzw-v2-pb-extras-sum-e uzw-v2-nowrap">${euro(extrasSub)}</span>
    </div>
  </td></tr></tfoot>
</table>`

    ablaufCore += pbDtl('Zusatzleistungen (Summe)', euro(extrasSub), true, extrasBody)
  }

  ablaufCore += `<div class="uzw-v2-pb-gtot" role="region" title="Gesamt (brutto, vor manuellen Rabatten)" aria-label="Gesamt brutto, vor manuellen Rabatten">
  <div class="uzw-v2-pb-gtot-in">
    <span class="uzw-v2-pb-gtot-l">Gesamt brutto</span>
    <span class="uzw-v2-pb-gtot-e uzw-v2-nowrap">${euro(grand)}</span>
  </div>
</div>`

  const discountStepLabel = (step: WidgetDiscountStep): string => {
    const n = String(step.note ?? '').trim()
    if (n) return n
    if (step.kind === 'percent') return `Rabatt · ${step.value} %`
    if (step.kind === 'amount') return `Rabatt · ${euro(step.value)}`
    return 'Rabatt'
  }
  const discTotal = pricing.discount_cents ?? 0
  const discountLines = (pricing.discount_steps ?? [])
    .filter((s) => !isUnderoccupancyStep(s))
    .map((s) => {
      const raw = discountStepLabel(s)
      const label = esc(raw)
      const isLong = label.length > 80
      const tClass = isLong ? ' uzw-v2-pb-rr-t--long' : ''
      return `<div class="uzw-v2-pb-rr">
  <div class="uzw-v2-pb-rr-t${tClass}">${label}</div>
  <div class="uzw-v2-pb-rr-a uzw-v2-nowrap">−${euro(s.discount_cents)}</div>
</div>`
    })
    .join('')

  const discountTotalBlock =
    discTotal > 0
      ? `<div class="uzw-v2-pb-rr uzw-v2-pb-rr--sum">
  <div class="uzw-v2-pb-rr-t">Rabatte gesamt</div>
  <div class="uzw-v2-pb-rr-a uzw-v2-nowrap">−${euro(discTotal)}</div>
</div>`
      : ''

  const discountSection =
    (pricing.discount_steps ?? []).filter((s) => !isUnderoccupancyStep(s)).length > 0 || discTotal > 0
      ? `<div class="uzw-v2-rabatte" role="region" aria-label="Rabatte">
  <p class="uzw-v2-rabatte-h">Rabatte (Codes und manuell, nacheinander)</p>
  <p class="uzw-v2-rabatte-hint">Jeder Rabatt bezieht sich auf den jeweiligen Zwischenbetrag (siehe Bezugsbetrag).</p>
  <div class="uzw-v2-pb-rr-list">
    ${discountLines}
    ${discountTotalBlock}
  </div>
</div>`
      : ''

  const { net: netGesamt, vat: vatGesamt } = netVatDisplayCents(pricing)
  const disc = discTotal
  const gNet = pricing.grand_total_net_cents
  const gVat = pricing.grand_total_vat_cents
  const netTitle = disc > 0 ? ' title="Steuerbar, nach manuellem Rabatt"' : ''
  const vatTitle = disc > 0 ? ' title="Nach manuellem Rabatt"' : ''
  const netRow =
    gNet != null
      ? `<div class="uzw-v2-ust-buchung-row uzw-v2-ust-buchung-row--stack"${netTitle}>
    <span class="uzw-v2-ust-buchung-txt">Netto</span>
    <span class="uzw-v2-ust-buchung-eur uzw-v2-nowrap">${euro(netGesamt)}</span>
  </div>`
      : ''
  const vatRow =
    gVat != null
      ? `<div class="uzw-v2-ust-buchung-row uzw-v2-ust-buchung-row--stack"${vatTitle}>
    <span class="uzw-v2-ust-buchung-txt">USt.</span>
    <span class="uzw-v2-ust-buchung-eur uzw-v2-nowrap">${euro(vatGesamt)}</span>
  </div>`
      : ''
  const vatRows = (pricing.vat_breakdown_by_rate ?? [])
    .map((v) => {
      const rateLabel = `${(v.vat_rate_bps / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`
      return `<tr><td class="uzw-v2-nowrap">${esc(rateLabel)}</td><td>${euro(v.net_cents)}</td><td>${euro(v.vat_cents)}</td><td>${euro(v.gross_cents)}</td></tr>`
    })
    .join('')

  const vatByRateBody =
    vatRows.length > 0
      ? `<table class="uzw-v2-price-table uzw-v2-pb-embed-tbl"><thead><tr><th>Satz</th><th>Netto</th><th>MwSt.</th><th>Brutto</th></tr></thead><tbody>${vatRows}</tbody></table>`
      : ''

  const hasVatByRate = vatRows.length > 0
  const hasUstBuchungSumme = gNet != null || gVat != null
  const ustTopBlock =
    !hasUstBuchungSumme && !hasVatByRate
      ? '<p class="uzw-v2-ust-buchung-miss">Keine Netto-USt. ausgewiesen.</p>'
      : hasUstBuchungSumme
        ? `${netRow}${vatRow}`
        : ''

  const ustByRateBlock = vatByRateBody
    ? `<div class="uzw-v2-ust-by-rate">
  <p class="uzw-v2-ust-by-cap">Nach Steuersätzen (Detail)</p>
  ${vatByRateBody}
</div>`
    : ''

  const combinedUstBody = `<div class="uzw-v2-ust-merged-bod" role="region" aria-label="USt. Aufschlüsselung">
  ${ustTopBlock ? `<div class="uzw-v2-ust-buchung-cols uzw-v2-ust-merged-cols">${ustTopBlock}</div>` : ''}
  ${ustByRateBlock}
</div>`

  const ustMeta = gVat != null || hasVatByRate ? euro(vatGesamt) : '—'

  const ustAufklaeppBlock = `<details class="uzw-v2-pb-dtl">
  <summary class="uzw-v2-pb-sum">
    <div class="uzw-v2-pb-sum-h">
      <span class="uzw-v2-pb-t">USt.-Aufschlüsselung (Buchung)</span>
      <span class="uzw-v2-pb-ch" aria-hidden="true">▾</span>
    </div>
    <div class="uzw-v2-pb-m uzw-v2-nowrap uzw-v2-pb-m--block">${esc(ustMeta)}</div>
  </summary>
  <div class="uzw-v2-pb-bod uzw-v2-pb-bod--ust">${combinedUstBody}</div>
</details>`

  const credit = pricing.credit_cents ?? 0
  const totalEnd = pricing.total_after_discount_cents ?? grand
  const nightsCounted = pricing.nights_counted ?? 0
  const creditBlock =
    credit > 0
      ? `<div class="uzw-v2-credit-row uzw-v2-credit-row--stack">
  <span class="uzw-v2-credit-l">Gutschrift</span>
  <span class="uzw-v2-credit uzw-v2-nowrap">−${euro(credit)}</span>
</div>`
      : ''
  const endsumme = `<div class="uzw-v2-endsumme uzw-v2-endsumme--stack" role="group" aria-label="Endsumme Buchung">
  <span class="uzw-v2-endsumme-l">Endsumme (Buchung)</span>
  <span class="uzw-v2-endsumme-v uzw-v2-nowrap"><strong>${euro(totalEnd)}</strong></span>
</div>`
  const nightsLine =
    nightsCounted > 0
      ? `<p class="uzw-v2-nights-meta">${nightsCounted} ${nightsCounted === 1 ? 'Nacht' : 'Nächte'} im Zeitraum</p>`
      : ''

  const secDeposit = pricing.security_deposit_cents ?? 0
  const secDepositLine =
    secDeposit > 0
      ? `<div class="uzw-v2-security-deposit" role="note">
  <span class="uzw-v2-security-deposit-l">Sicherheitskaution</span>
  <span class="uzw-v2-security-deposit-v uzw-v2-nowrap">${euro(secDeposit)}</span>
  ${pricing.security_deposit_due_days_before_arrival != null ? `<p class="uzw-v2-security-deposit-hint">Fällig ${pricing.security_deposit_due_days_before_arrival} Tage vor Anreise · wird nach dem Aufenthalt zurückgezahlt.</p>` : ''}
</div>`
      : ''

  const notesList = (pricing.notes || [])
    .map((n) => `<li class="uzw-v2-pricing-note-li">${esc(n)}</li>`)
    .join('')
  const notesBlock = notesList
    ? `<ul class="uzw-v2-pricing-notes" role="list">${notesList}</ul>`
    : ''

  return `<div class="uzw-v2-pricing-app">
  <p class="uzw-v2-pricing-ablauf-h">Ablauf der Beträge (oben &rarr; unten)</p>
  <div class="uzw-v2-pb-ablauf-inn">
    ${ablaufCore}
    ${discountSection}
    ${ustAufklaeppBlock}
  </div>
</div>
    ${creditBlock}
    ${endsumme}
    ${secDepositLine}
    ${nightsLine}
    ${notesBlock}`
}

async function fetchWidgetStayConstraints(
  apiBaseUrl: string,
  token: string,
  propertyId: string,
): Promise<WidgetStayConstraints | null> {
  const base = apiBaseUrl.replace(/\/$/, '')
  const fromIso = todayYmd()
  const toIso = formatYmd(parseYmd(fromIso).add(2, 'year'))
  const res = await fetch(
    `${base}/api/v1/public/widget/properties/${encodeURIComponent(propertyId)}/stay-constraints?from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`,
    { headers: { Accept: 'application/json', Authorization: `Bearer ${token}` } },
  )
  if (!res.ok) return null
  return (await res.json()) as WidgetStayConstraints
}

/** Buchungs-UI (Reisezeit, Extras, Preis, Checkout) an ein gerendertes Detail-Widget haengen. */
export async function bindDetailBooking(
  host: HTMLElement,
  opts: WidgetV2MountOptions,
  propertyId: string,
  propertyNumber: string,
): Promise<void> {
  const travelForm = host.querySelector('[data-detail-travel-form]') as HTMLFormElement | null
  const validationEl = host.querySelector('[data-detail-validation]') as HTMLElement | null
  const extrasEl = host.querySelector('[data-detail-extras]') as HTMLElement | null
  const pricingEl = host.querySelector('[data-detail-pricing]') as HTMLElement | null
  const checkoutForm = host.querySelector('[data-detail-checkout-form]') as HTMLFormElement | null
  const checkoutStatusEl = host.querySelector('[data-detail-checkout-status]') as HTMLElement | null
  const noticeEl = host.querySelector('[data-detail-checkout-notice]') as HTMLElement | null
  const asideEl = host.querySelector('[data-detail-aside]') as HTMLElement | null
  const postCheckoutRemove =
    (host.querySelector('[data-detail-post-checkout-remove]') as HTMLElement | null) ?? asideEl
  const layoutEl = host.querySelector('.uzw-v2-detail-layout') as HTMLElement | null
  const checkoutModal = host.querySelector('[data-detail-checkout-modal]') as HTMLElement | null
  const openCheckoutBtn = host.querySelector('[data-detail-checkout-open]') as HTMLButtonElement | null

  if (!travelForm || !extrasEl || !pricingEl || !checkoutForm) return

  const extrasFold = host.querySelector<HTMLDetailsElement>('[data-detail-extras-fold]')
  if (extrasFold) {
    extrasFold.removeAttribute('open')
    extrasFold.open = false
  }

  let stayConstraints: WidgetStayConstraints | null = null
  try {
    stayConstraints = await fetchWidgetStayConstraints(opts.apiBaseUrl, opts.token, propertyId)
  } catch {
    stayConstraints = null
  }

  const setValidation = (message: string, tone: 'neutral' | 'ok' | 'error' = 'neutral') => {
    if (!validationEl) return
    validationEl.textContent = message
    if (tone === 'ok') validationEl.style.color = '#166534'
    else if (tone === 'error') validationEl.style.color = '#b91c1c'
    else validationEl.style.color = '#64748b'
  }

  const setCheckoutStatus = (msg: string, tone: 'neutral' | 'ok' | 'error' = 'neutral') => {
    if (!checkoutStatusEl) return
    checkoutStatusEl.textContent = msg
    if (tone === 'ok') checkoutStatusEl.style.color = '#166534'
    else if (tone === 'error') checkoutStatusEl.style.color = '#b91c1c'
    else checkoutStatusEl.style.color = '#64748b'
  }

  const adultInput = travelForm.querySelector('[name="adult_count"]') as HTMLInputElement | null
  const childInput = travelForm.querySelector('[name="child_count"]') as HTMLInputElement | null
  const fromInput = travelForm.querySelector('[name="stay_from"]') as HTMLInputElement | null
  const toInput = travelForm.querySelector('[name="stay_to"]') as HTMLInputElement | null
  const promoCodeInput = host.querySelector('[data-detail-promo-code]') as HTMLInputElement | null
  const promoEmailInput = host.querySelector('[data-detail-promo-email]') as HTMLInputElement | null
  const promoEmailRow =
    (host.querySelector('[data-detail-promo-email-row]') as HTMLElement | null) ??
    (promoEmailInput?.closest('.uzw-v2-field') as HTMLElement | null) ??
    null
  const promoEmailHint =
    (host.querySelector('[data-detail-promo-email-hint]') as HTMLElement | null) ??
    (promoEmailRow?.parentElement?.querySelector('.uzw-v2-detail-promo-hint') as HTMLElement | null) ??
    null
  let promoNeedsEmail = false

  const syncPromoEmailVisibility = () => {
    const hasPromoCode = String(promoCodeInput?.value ?? '').trim().length > 0
    const hasPromoEmail = String(promoEmailInput?.value ?? '').trim().length > 0
    const visible = hasPromoCode && (promoNeedsEmail || hasPromoEmail)
    if (promoEmailRow) promoEmailRow.style.display = visible ? '' : 'none'
    if (promoEmailHint) promoEmailHint.style.display = visible ? '' : 'none'
  }

  enhanceNumberSteppers(host)
  enhanceDateRangePicker({ form: travelForm, constraints: stayConstraints })

  if (typeof window !== 'undefined') {
    try {
      const u = new URL(window.location.href)
      const checkout = String(u.searchParams.get('checkout') ?? '').trim().toLowerCase()
      const bookingId = String(u.searchParams.get('booking_id') ?? '').trim()
      if (checkout === 'success' || checkout === 'cancel' || bookingId) {
        const text =
          checkout === 'success' || bookingId
            ? `Buchung erstellt${bookingId ? ` (Referenz ${bookingId})` : ''}. Wir melden uns mit den naechsten Schritten.`
            : `Checkout wurde abgebrochen. Du kannst den Vorgang erneut starten.`
        if (noticeEl) {
          noticeEl.className = `uzw-v2-detail-checkout-notice ${checkout === 'success' || bookingId ? 'is-success' : 'is-cancel'}`
          noticeEl.textContent = text
          noticeEl.style.display = ''
        }
        if (checkout === 'success' || bookingId) {
          layoutEl?.classList.add('uzw-v2-detail-layout--single')
          postCheckoutRemove?.remove()
        }
        u.searchParams.delete('checkout')
        if (history.replaceState) history.replaceState(null, '', u.pathname + u.search + u.hash)
      }
    } catch {
      // ignore
    }
  }

  let validationTimer: number | null = null
  let validationSeq = 0
  let extrasSeq = 0
  let checkoutPending = false
  const extraQty = new Map<string, number>()

  /** Public-API „passt zur Unterkunft“-Check (Filter-Schnellabfrage). */
  let propertyFilterOk = false
  let propertyFilterPending = false
  /** Letzter erfolgreicher POST preview-pricing für aktuelle Eingaben. */
  let pricingPreviewOk = false
  /** Extras-Liste ohne Ladefehler (oder leer erlaubt). */
  let extrasRenderOk = true

  const todayIsoLocal = (): string => todayYmd()

  const stayConstraintViolation = (): string | null => {
    if (!stayConstraints) return null
    const stayFrom = String(fromInput?.value ?? '').trim()
    const stayTo = String(toInput?.value ?? '').trim()
    if (!stayFrom || !stayTo) return null
    const ctx = buildConstraintCtx(stayConstraints, todayIsoLocal())
    if (isStartDisabled(stayFrom, ctx)) return startDisabledReason(stayFrom, ctx) || 'Anreise nicht erlaubt.'
    if (isEndDisabled(stayTo, stayFrom, ctx)) return endDisabledReason(stayTo, stayFrom, ctx) || 'Abreise nicht erlaubt.'
    return null
  }

  const getCheckoutBlockedReason = (): string | null => {
    const { localError } = buildValidationQuery()
    if (localError) return localError
    const stayFrom = String(fromInput?.value ?? '').trim()
    const stayTo = String(toInput?.value ?? '').trim()
    if (!stayFrom || !stayTo) return 'Bitte An- und Abreise waehlen.'
    const sc = stayConstraintViolation()
    if (sc) return sc
    if (!extrasRenderOk) return 'Zusatzleistungen konnten nicht geladen werden. Bitte Seite neu laden.'
    if (propertyFilterPending) return 'Auswahl wird geprueft ...'
    if (!propertyFilterOk) return 'Auswahl passt nicht zur Unterkunft oder Eingaben fehlen.'
    if (!pricingPreviewOk) return 'Preisvorschau fehlgeschlagen oder Aufenthalt ungueltig. Bitte Reisezeit pruefen.'
    return null
  }

  const syncCheckoutGate = (): void => {
    const blocked = getCheckoutBlockedReason()
    if (openCheckoutBtn) {
      openCheckoutBtn.disabled = !!blocked
      openCheckoutBtn.title = blocked || ''
    }
    const submitBtn = checkoutForm.querySelector('button[type="submit"]') as HTMLButtonElement | null
    if (submitBtn) {
      submitBtn.disabled = !!blocked
      submitBtn.title = blocked || ''
    }
  }

  const buildValidationQuery = (): { query?: Record<string, string>; localError?: string } => {
    const adults = String(adultInput?.value ?? '').trim()
    const children = String(childInput?.value ?? '').trim()
    const stayFrom = String(fromInput?.value ?? '').trim()
    const stayTo = String(toInput?.value ?? '').trim()
    if (children !== '' && adults === '') return { localError: 'Bitte Erwachsene setzen, wenn Kinder gesetzt sind.' }
    if ((stayFrom || stayTo) && (!stayFrom || !stayTo)) {
      return { localError: 'Bitte Anreise und Abreise gemeinsam ausfuellen (oder beide leer lassen).' }
    }
    if (stayFrom && stayTo && stayFrom >= stayTo) {
      return { localError: 'Anreise muss vor Abreise liegen.' }
    }
    const query: Record<string, string> = {}
    if (propertyNumber) query.q = propertyNumber
    if (adults !== '') query.adult_count = adults
    if (children !== '') query.child_count = children
    if (stayFrom !== '' && stayTo !== '') {
      query.stay_from = stayFrom
      query.stay_to = stayTo
    }
    return { query }
  }

  const runLiveValidation = async () => {
    const { query, localError } = buildValidationQuery()
    if (localError) {
      propertyFilterOk = false
      propertyFilterPending = false
      setValidation(localError, 'error')
      syncCheckoutGate()
      return
    }
    if (!query || (!query.adult_count && !query.child_count && !query.stay_from && !query.stay_to)) {
      propertyFilterOk = false
      propertyFilterPending = false
      setValidation('', 'neutral')
      syncCheckoutGate()
      return
    }
    const seq = ++validationSeq
    propertyFilterPending = true
    propertyFilterOk = false
    setValidation('Pruefe Auswahl ...', 'neutral')
    syncCheckoutGate()
    try {
      const data = await fetchPropertiesQuick(opts.apiBaseUrl, opts.token, query)
      if (seq !== validationSeq) return
      const valid = data.items.some((item) => item.id === propertyId)
      propertyFilterOk = valid
      setValidation(
        valid ? 'Auswahl ist fuer diese Unterkunft gueltig.' : 'Auswahl passt nicht zur Unterkunft.',
        valid ? 'ok' : 'error',
      )
    } catch (e) {
      if (seq !== validationSeq) return
      propertyFilterOk = false
      setValidation(e instanceof Error ? e.message : String(e), 'error')
    } finally {
      if (seq === validationSeq) propertyFilterPending = false
      syncCheckoutGate()
    }
  }

  const scheduleLiveValidation = (delayMs: number) => {
    if (validationTimer != null) window.clearTimeout(validationTimer)
    validationTimer = window.setTimeout(() => {
      validationTimer = null
      void runLiveValidation()
    }, delayMs)
  }

  const runPricingPreview = async () => {
    const adults = Number(String(adultInput?.value ?? '').trim() || '0')
    const children = Number(String(childInput?.value ?? '').trim() || '0')
    const stayFrom = String(fromInput?.value ?? '').trim()
    const stayTo = String(toInput?.value ?? '').trim()
    if (!stayFrom || !stayTo) {
      pricingPreviewOk = false
      pricingEl.innerHTML = '<p class="uzw-v2-meta">Fuer Preisvorschau bitte An- und Abreise setzen.</p>'
      syncCheckoutGate()
      return
    }
    const extra_lines = Array.from(extraQty.entries())
      .map(([extra_service_id, quantity]) => ({ extra_service_id, quantity: Math.max(0, Math.floor(quantity)) }))
      .filter((l) => l.quantity > 0)
    const promoRaw = String(promoCodeInput?.value ?? '').trim()
    const promoEmailRaw = String(promoEmailInput?.value ?? '').trim()
    if (!promoRaw) {
      promoNeedsEmail = false
      syncPromoEmailVisibility()
    }
    try {
      const pricing = await previewWidgetPricing(opts.apiBaseUrl, opts.token, propertyId, {
        adult_count: adults > 0 ? adults : undefined,
        child_count: children > 0 ? children : undefined,
        stay_from: stayFrom,
        stay_to: stayTo,
        extra_lines,
        ...(promoRaw
          ? {
              promo_code: promoRaw,
              ...(promoEmailRaw.includes('@') ? { customer_email: promoEmailRaw } : {}),
            }
          : {}),
      })
      if (promoRaw) {
        // Wenn die Vorschau mit Code ohne E-Mail klappt, ist kein E-Mail-Scope nötig.
        promoNeedsEmail = false
      }
      syncPromoEmailVisibility()
      pricingEl.innerHTML = renderPricingHtml(pricing)
      pricingPreviewOk = true
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      if (
        promoRaw &&
        (msg.includes('an eine E-Mail gebunden') ||
          msg.includes('andere E-Mail-Adresse') ||
          msg.includes('Rabattcode: E-Mail fehlt'))
      ) {
        promoNeedsEmail = true
      }
      syncPromoEmailVisibility()
      pricingPreviewOk = false
      pricingEl.innerHTML = `<p class="uzw-v2-meta" style="color:#b91c1c">${esc(msg)}</p>`
    }
    syncCheckoutGate()
  }

  const schedulePricing = debounce(() => {
    void runPricingPreview()
  }, 350)

  const renderExtras = async () => {
    const adults = String(adultInput?.value ?? '').trim()
    const children = String(childInput?.value ?? '').trim()
    const stayFrom = String(fromInput?.value ?? '').trim()
    const stayTo = String(toInput?.value ?? '').trim()
    const query: Record<string, string> = {}
    if (adults) query.adult_count = adults
    if (children) query.child_count = children
    if (stayFrom && stayTo) {
      query.stay_from = stayFrom
      query.stay_to = stayTo
    }
    const seq = ++extrasSeq
    extrasEl.innerHTML = '<p class="uzw-v2-meta">Lade Zusatzleistungen ...</p>'
    try {
      const services = await fetchWidgetExtraServices(opts.apiBaseUrl, opts.token, propertyId, query)
      if (seq !== extrasSeq) {
        syncCheckoutGate()
        return
      }
      extrasRenderOk = true
      if (services.length === 0) {
        extrasEl.innerHTML = '<p class="uzw-v2-meta">Keine Zusatzleistungen verfuegbar.</p>'
        await runPricingPreview()
        syncCheckoutGate()
        return
      }
      const extraMaxDisplayCap = 9999
      const extraMaxLabel = (q: number): string => (q > extraMaxDisplayCap ? 'ohne praktisches Limit' : String(q))
      const rows = services
        .map((s) => {
          const current = Math.max(0, extraQty.get(s.id) ?? (s.required ? s.min_quantity : 0))
          const safeCurrent = Math.min(Math.max(current, s.required ? s.min_quantity : 0), s.effective_max_quantity)
          extraQty.set(s.id, safeCurrent)
          const unit = extraServiceBillingLabel(s.billing_unit)
          const maxAttr = s.effective_max_quantity > 100_000 ? '' : ` max="${s.effective_max_quantity}"`
          const requiredBadge = s.required ? '<span class="uzw-v2-required-badge">Pflicht</span>' : ''
          return `<label class="uzw-v2-field uzw-v2-extra-card">
            <span class="uzw-v2-extra-head"><span style="font-weight:600">${esc(s.name)}${requiredBadge}</span></span>
            <span class="uzw-v2-extra-meta">${esc(unit)} · ${euro(s.unit_price_cents)} · min ${s.required ? s.min_quantity : 0} / max ${esc(extraMaxLabel(s.effective_max_quantity))}</span>
            <input data-extra-id="${esc(s.id)}" data-min="${s.required ? s.min_quantity : 0}" data-max="${s.effective_max_quantity}" data-uzw-v2-stepper type="number" min="${s.required ? s.min_quantity : 0}"${maxAttr} value="${safeCurrent}" />
          </label>`
        })
        .join('')
      extrasEl.innerHTML = `<div class="uzw-v2-detail-extras-grid">${rows}</div>`
      enhanceNumberSteppers(extrasEl)
      extrasEl.querySelectorAll('input[data-extra-id]').forEach((node) => {
        const inp = node as HTMLInputElement
        const extraId = String(inp.getAttribute('data-extra-id') || '')
        const min = Number(inp.getAttribute('data-min') || '0')
        const max = Number(inp.getAttribute('data-max') || '999999')
        const clampValue = () => {
          const raw = Number.isFinite(Number(inp.value)) ? Number(inp.value) : 0
          const clamped = Math.min(Math.max(Math.floor(raw), min), max)
          inp.value = String(clamped)
          extraQty.set(extraId, clamped)
        }
        inp.addEventListener('change', clampValue)
        inp.addEventListener('input', () => {
          clampValue()
          schedulePricing()
        })
      })
      await runPricingPreview()
    } catch (e) {
      if (seq !== extrasSeq) return
      extrasRenderOk = false
      pricingPreviewOk = false
      extrasEl.innerHTML = `<p class="uzw-v2-meta" style="color:#b91c1c">${esc(e instanceof Error ? e.message : String(e))}</p>`
      syncCheckoutGate()
    }
  }

  const scheduleExtras = debounce(() => {
    void renderExtras()
  }, 300)

  travelForm.addEventListener('input', () => {
    scheduleExtras()
    schedulePricing()
    scheduleLiveValidation(400)
    persistDetailQuery()
    syncCheckoutGate()
  })
  travelForm.addEventListener('change', () => {
    scheduleExtras()
    schedulePricing()
    scheduleLiveValidation(400)
    persistDetailQuery()
    syncCheckoutGate()
  })

  for (const el of [promoCodeInput, promoEmailInput]) {
    if (!el) continue
    el.addEventListener('input', () => {
      if (el === promoCodeInput && String(promoCodeInput?.value ?? '').trim() === '') {
        promoNeedsEmail = false
      }
      syncPromoEmailVisibility()
      schedulePricing()
      syncCheckoutGate()
    })
  }

  function persistDetailQuery(): void {
    if (typeof window === 'undefined') return
    try {
      const u = new URL(window.location.href)
      const pairs: Array<[string, HTMLInputElement | null]> = [
        ['adult_count', adultInput],
        ['child_count', childInput],
        ['stay_from', fromInput],
        ['stay_to', toInput],
      ]
      for (const [key, input] of pairs) {
        const val = String(input?.value ?? '').trim()
        if (val) u.searchParams.set(key, val)
        else u.searchParams.delete(key)
      }
      if (!u.searchParams.get('id')) u.searchParams.set('id', propertyId)
      if (history.replaceState) history.replaceState(null, '', u.pathname + u.search + u.hash)
    } catch {
      // ignore
    }
  }

  async function submitCheckout() {
    if (checkoutPending) return
    const gate = getCheckoutBlockedReason()
    if (gate) {
      setCheckoutStatus(gate, 'error')
      return
    }
    const emailInput = checkoutForm.querySelector('[name="email"]') as HTMLInputElement | null
    const firstNameInput = checkoutForm.querySelector('[name="first_name"]') as HTMLInputElement | null
    const lastNameInput = checkoutForm.querySelector('[name="last_name"]') as HTMLInputElement | null
    const phoneInput = checkoutForm.querySelector('[name="phone"]') as HTMLInputElement | null
    const streetLine1Input = checkoutForm.querySelector('[name="street_line1"]') as HTMLInputElement | null
    const streetLine2Input = checkoutForm.querySelector('[name="street_line2"]') as HTMLInputElement | null
    const postalCodeInput = checkoutForm.querySelector('[name="postal_code"]') as HTMLInputElement | null
    const cityInput = checkoutForm.querySelector('[name="city"]') as HTMLInputElement | null
    const countryInput = checkoutForm.querySelector('[name="country"]') as HTMLSelectElement | null
    const privacyInput = checkoutForm.querySelector('[name="privacy_accepted"]') as HTMLInputElement | null
    const adults = Number(String(adultInput?.value ?? '').trim() || '0')
    const children = Number(String(childInput?.value ?? '').trim() || '0')
    const stayFrom = String(fromInput?.value ?? '').trim()
    const stayTo = String(toInput?.value ?? '').trim()
    if (!emailInput || !privacyInput) return
    if (!emailInput.value.trim()) {
      setCheckoutStatus('Bitte E-Mail angeben.', 'error')
      return
    }
    if (!String(firstNameInput?.value ?? '').trim()) {
      setCheckoutStatus('Bitte Vorname angeben.', 'error')
      return
    }
    if (!String(lastNameInput?.value ?? '').trim()) {
      setCheckoutStatus('Bitte Nachname angeben.', 'error')
      return
    }
    if (!String(streetLine1Input?.value ?? '').trim()) {
      setCheckoutStatus('Bitte Strasse und Hausnummer angeben.', 'error')
      return
    }
    if (!String(postalCodeInput?.value ?? '').trim()) {
      setCheckoutStatus('Bitte PLZ angeben.', 'error')
      return
    }
    if (!String(cityInput?.value ?? '').trim()) {
      setCheckoutStatus('Bitte Ort angeben.', 'error')
      return
    }
    if (!String(countryInput?.value ?? '').trim()) {
      setCheckoutStatus('Bitte Land auswaehlen.', 'error')
      return
    }
    if (!stayFrom || !stayTo) {
      setCheckoutStatus('Bitte An- und Abreise waehlen.', 'error')
      return
    }
    if (!privacyInput.checked) {
      setCheckoutStatus('Bitte Datenschutzhinweis akzeptieren.', 'error')
      return
    }
    const extra_lines = Array.from(extraQty.entries())
      .map(([extra_service_id, quantity]) => ({ extra_service_id, quantity: Math.max(0, Math.floor(quantity)) }))
      .filter((l) => l.quantity > 0)
    checkoutPending = true
    setCheckoutStatus('Checkout wird vorbereitet ...', 'neutral')
    try {
      const baseUrl = typeof window !== 'undefined' ? new URL(window.location.href) : null
      const successUrl = (() => {
        if (!baseUrl) return undefined
        const u = new URL(baseUrl.toString())
        u.searchParams.set('checkout', 'success')
        return u.toString()
      })()
      const cancelUrl = (() => {
        if (!baseUrl) return undefined
        const u = new URL(baseUrl.toString())
        u.searchParams.set('checkout', 'cancel')
        return u.toString()
      })()
      const promoForCheckout = String(promoCodeInput?.value ?? '').trim()
      const preparePayload: Record<string, unknown> = {
        property_id: propertyId,
        adult_count: adults > 0 ? adults : undefined,
        child_count: children > 0 ? children : undefined,
        stay_from: stayFrom,
        stay_to: stayTo,
        extra_lines,
        guest: {
          email: emailInput.value.trim(),
          first_name: String(firstNameInput?.value ?? '').trim() || undefined,
          last_name: String(lastNameInput?.value ?? '').trim() || undefined,
          phone: String(phoneInput?.value ?? '').trim() || undefined,
          street_line1: String(streetLine1Input?.value ?? '').trim() || undefined,
          street_line2: String(streetLine2Input?.value ?? '').trim() || undefined,
          postal_code: String(postalCodeInput?.value ?? '').trim() || undefined,
          city: String(cityInput?.value ?? '').trim() || undefined,
          country: String(countryInput?.value ?? '').trim() || undefined,
          privacy_accepted: true,
        },
        success_url: successUrl,
        cancel_url: cancelUrl,
      }
      if (promoForCheckout) {
        preparePayload.promo_code = promoForCheckout
      }
      const prepared = await prepareWidgetCheckout(opts.apiBaseUrl, opts.token, preparePayload)
      setCheckoutStatus(
        prepared.customer_status === 'existing_customer'
          ? 'Bestandskunde erkannt. Weiterleitung ...'
          : 'Checkout wird gestartet ...',
        'neutral',
      )
      const confirmed = await confirmWidgetCheckout(opts.apiBaseUrl, opts.token, prepared.checkout_token)
      setCheckoutStatus('Weiterleitung ...', 'ok')
      if (typeof window !== 'undefined') window.location.href = confirmed.redirect_url
    } catch (e) {
      setCheckoutStatus(e instanceof Error ? e.message : String(e), 'error')
    } finally {
      checkoutPending = false
    }
  }

  checkoutForm.addEventListener('submit', (ev) => {
    ev.preventDefault()
    void submitCheckout()
  })

  const closeCheckoutModal = () => {
    if (!checkoutModal) return
    checkoutModal.setAttribute('hidden', '')
    checkoutModal.setAttribute('aria-hidden', 'true')
    if (typeof document !== 'undefined') document.body.style.overflow = ''
  }
  const openCheckoutModal = () => {
    if (!checkoutModal) return
    const gate = getCheckoutBlockedReason()
    if (gate) {
      setCheckoutStatus(gate, 'error')
      return
    }
    checkoutModal.removeAttribute('hidden')
    checkoutModal.setAttribute('aria-hidden', 'false')
    if (typeof document !== 'undefined') document.body.style.overflow = 'hidden'
  }

  if (checkoutModal && openCheckoutBtn) {
    openCheckoutBtn.addEventListener('click', () => {
      openCheckoutModal()
    })
    for (const el of checkoutModal.querySelectorAll('[data-detail-checkout-modal-close]')) {
      el.addEventListener('click', () => closeCheckoutModal())
    }
    const onDocKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (checkoutModal.hasAttribute('hidden')) return
      closeCheckoutModal()
    }
    window.addEventListener('keydown', onDocKey)
  }

  syncCheckoutGate()
  syncPromoEmailVisibility()
  void renderExtras().finally(() => {
    syncCheckoutGate()
  })
  void runLiveValidation().finally(() => {
    syncCheckoutGate()
  })
}
